import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { announcementsApi } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { Card, CardHeader, CardTitle, CardBody } from '../ui/Card';
import Button from '../ui/Button';
import Input, { Textarea, Select, Label } from '../ui/Input';
import Badge from '../ui/Badge';
import Modal from '../ui/Modal';
import { formatDateShort } from '../../lib/dateUtils';
import { Plus, Edit, Trash2, Pin, Megaphone, AlertTriangle } from 'lucide-react';

const emptyForm = { title: '', content: '', category: 'announcement', isPinned: false, isActive: true };

// 最新動態: 站務公告 / 注意事項, managed by admin / 處長室 / 工安組
export default function AnnouncementsCard() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const queryClient = useQueryClient();
  const { hasFullAccess } = useAuthStore();
  const canManage = hasFullAccess();

  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);

  const { data: announcements } = useQuery({
    queryKey: ['announcements', canManage],
    queryFn: () => announcementsApi.getAll(canManage ? { all: 'true' } : {}),
    select: (res) => res.data,
  });

  const saveMutation = useMutation({
    mutationFn: (data) => (editModal.item
      ? announcementsApi.update(editModal.item.id, data)
      : announcementsApi.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries(['announcements']);
      setEditModal({ open: false, item: null });
    },
    onError: (error) => setFormError(error.response?.data?.error || t('errors.generic', '操作失敗')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => announcementsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['announcements']);
      setDeleteModal({ open: false, item: null });
    },
  });

  const openEdit = (item = null) => {
    setForm(item
      ? {
          title: item.title,
          content: item.content || '',
          category: item.category,
          isPinned: Boolean(item.isPinned),
          isActive: Boolean(item.isActive),
        }
      : emptyForm);
    setFormError(null);
    setEditModal({ open: true, item });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setFormError(t('errors.required'));
      return;
    }
    saveMutation.mutate(form);
  };

  const categoryBadge = (category) => (category === 'announcement'
    ? <Badge className="bg-blue-100 text-blue-800"><Megaphone className="w-3 h-3 mr-1" />{t('announcements.announcement', '站務公告')}</Badge>
    : <Badge className="bg-amber-100 text-amber-800"><AlertTriangle className="w-3 h-3 mr-1" />{t('announcements.notice', '注意事項')}</Badge>);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('dashboard.recentActivity')}</CardTitle>
          {canManage && (
            <Button variant="outline" size="sm" onClick={() => openEdit()}>
              <Plus className="w-4 h-4 mr-1" />
              {t('announcements.new', '新增公告')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {announcements?.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {announcements.map((item) => (
              <li key={item.id} className={`px-6 py-4 ${item.isActive ? '' : 'opacity-50'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      {item.isPinned ? <Pin className="w-4 h-4 text-red-500" /> : null}
                      {categoryBadge(item.category)}
                      <span className="font-medium text-gray-900">{item.title}</span>
                      {!item.isActive && <Badge>{t('announcements.hidden', '未顯示')}</Badge>}
                    </div>
                    {item.content && (
                      <p className="text-gray-600 whitespace-pre-wrap">{item.content}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDateShort(item.updatedAt, locale)}
                      {item.createdByName && ` · ${item.createdByName}`}
                    </p>
                  </div>
                  {canManage && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, item })}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-center text-gray-500 py-8">{t('announcements.empty', '目前沒有公告')}</p>
        )}
      </CardBody>

      {/* Create / edit */}
      <Modal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, item: null })}
        title={editModal.item ? t('announcements.edit', '編輯公告') : t('announcements.new', '新增公告')}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('announcements.category', '類別')}</Label>
              <Select value={form.category} onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}>
                <option value="announcement">{t('announcements.announcement', '站務公告')}</option>
                <option value="notice">{t('announcements.notice', '注意事項')}</option>
              </Select>
            </div>
            <div>
              <Label>{t('announcements.display', '顯示')}</Label>
              <Select
                value={form.isActive ? 'true' : 'false'}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.value === 'true' }))}
              >
                <option value="true">{t('announcements.visible', '顯示')}</option>
                <option value="false">{t('announcements.hidden', '未顯示')}</option>
              </Select>
            </div>
          </div>
          <div>
            <Label required>{t('announcements.title', '標題')}</Label>
            <Input value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} />
          </div>
          <div>
            <Label>{t('announcements.content', '內容')}</Label>
            <Textarea
              rows={5}
              value={form.content}
              onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.isPinned}
              onChange={(e) => setForm((prev) => ({ ...prev, isPinned: e.target.checked }))}
            />
            {t('announcements.pinned', '置頂')}
          </label>
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setEditModal({ open: false, item: null })}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={saveMutation.isPending}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, item: null })}
        title={t('common.confirm')}
        size="sm"
      >
        <div className="space-y-4">
          <p>{t('announcements.deleteConfirm', '確定要刪除此公告嗎？')}</p>
          <p className="text-gray-500">{deleteModal.item?.title}</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteModal({ open: false, item: null })}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(deleteModal.item?.id)}
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
